import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuicklinks } from './useQuicklinks';
import type { Quicklink } from './quicklinks';

const QuicklinksUI = lazy(() => import('./QuicklinksUI'));

export interface QuicklinksLauncherProps {
  /** Manager open state (owned by the Sidebar, so the rail button can open it). */
  managerOpen: boolean;
  onOpenManager: () => void;
  onCloseManager: () => void;
}

/**
 * Connected quicklinks glue, mounted in the persistent Sidebar. Owns the data
 * hook, navigation, the "add current page" action, and the global shortcut
 * (Cmd/Ctrl+Shift+K opens the manager; adding is the button inside it). The
 * heavy UI renders only while the manager is open (lazy chunk, so the full
 * lucide set stays out of the main bundle).
 */
export const QuicklinksLauncher = ({
  managerOpen,
  onOpenManager,
  onCloseManager,
}: QuicklinksLauncherProps) => {
  const navigate = useNavigate();
  const { links, add, remove, edit, setIcon, setColor, move } = useQuicklinks();
  // The link just added via "Add current page" opens in edit mode so the user
  // can review/adjust it before it's final.
  const [editingId, setEditingId] = useState<string>();

  const addCurrent = useCallback(() => {
    setEditingId(add({ title: document.title, url: window.location.href }));
  }, [add]);

  // Reset the auto-edit target when the manager closes, so a later open shows
  // every row in view mode (the edit form is only for a just-added link).
  useEffect(() => {
    if (!managerOpen) setEditingId(undefined);
  }, [managerOpen]);

  // Single shortcut: Cmd/Ctrl+Shift+K opens the manager. Adding a page is the
  // button inside the popup. Ignored while typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const typing =
        !!el &&
        (el.tagName === 'INPUT' ||
          el.tagName === 'TEXTAREA' ||
          el.isContentEditable);
      if (!(e.metaKey || e.ctrlKey) || !e.shiftKey || typing) return;
      if (e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpenManager();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onOpenManager]);

  const openLink = useCallback(
    (l: Quicklink, newTab: boolean) => {
      let target: URL | null = null;
      try {
        target = new URL(l.url, window.location.origin);
      } catch {
        target = null;
      }
      // External (or unparseable) urls open in a new tab; same-origin urls
      // SPA-navigate so the portal doesn't do a full reload.
      if (newTab || !target || target.origin !== window.location.origin) {
        window.open(l.url, '_blank', 'noopener,noreferrer');
        return;
      }
      navigate(`${target.pathname}${target.search}${target.hash}`);
      onCloseManager();
    },
    [navigate, onCloseManager],
  );

  return (
    <>
      {managerOpen && (
        <Suspense fallback={null}>
          <QuicklinksUI
            managerOpen={managerOpen}
            links={links}
            editingId={editingId}
            onClose={onCloseManager}
            onAddCurrent={addCurrent}
            onOpen={l => openLink(l, false)}
            onOpenNewTab={l => openLink(l, true)}
            onEdit={edit}
            onSetIcon={setIcon}
            onSetColor={setColor}
            onMove={move}
            onDelete={remove}
          />
        </Suspense>
      )}
    </>
  );
};
